import React, { useState } from 'react'
import { Book, ExternalLink, Code, FileJson, Shield, AlertCircle, Info, Key, Globe } from 'lucide-react'

export const ApiDocumentation: React.FC = () => {
  const [showAuthNote, setShowAuthNote] = useState(false)

  const apiDocs = [
    {
      title: 'Swagger UI',
      description: 'Interactive API documentation with try-it-out functionality',
      url: '/api/docs',
      icon: Code,
      color: 'bg-green-500',
      features: [
        'Interactive API testing',
        'Request/response examples',
        'Authentication testing',
        'Parameter validation'
      ]
    },
    {
      title: 'ReDoc',
      description: 'Clean, responsive API documentation with search',
      url: '/api/docs',
      icon: Book,
      color: 'bg-blue-500',
      features: [
        'Beautiful documentation',
        'Full-text search',
        'Code samples',
        'Responsive design'
      ]
    },
    {
      title: 'OpenAPI Spec',
      description: 'Raw OpenAPI 3.0 specification in JSON format',
      url: '/api/docs/openapi.yaml',
      icon: FileJson,
      color: 'bg-purple-500',
      features: [
        'Machine-readable format',
        'Import to Postman',
        'Client SDK generation',
        'API contract testing'
      ]
    }
  ]

  const securityNotes = [
    {
      title: 'Authentication',
      description: 'Most API endpoints require JWT authentication via Bearer token',
      icon: Key
    },
    {
      title: 'Public Access',
      description: 'Documentation endpoints are currently public for development',
      icon: Globe
    },
    {
      title: 'Rate Limiting',
      description: 'API endpoints have rate limiting to prevent abuse',
      icon: Shield
    }
  ]

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">API Documentation</h2>
            <p className="text-gray-600 mt-1">Access interactive API documentation and specifications</p>
          </div>
          <Book className="w-8 h-8 text-blue-500" />
        </div>

        {/* Documentation Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {apiDocs.map((doc) => (
            <div key={doc.url} className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`${doc.color} p-3 rounded-lg`}>
                  <doc.icon className="w-6 h-6 text-white" />
                </div>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-white text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors"
                >
                  Open <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{doc.title}</h3>
              <p className="text-gray-600 text-sm mb-4">{doc.description}</p>
              
              <div className="space-y-1">
                {doc.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                    <span className="text-xs text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Security Information */}
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Security & Access Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {securityNotes.map((note) => (
                  <div key={note.title} className="flex items-start gap-3">
                    <note.icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900 text-sm">{note.title}</h4>
                      <p className="text-xs text-gray-600 mt-0.5">{note.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <button
            onClick={() => setShowAuthNote(!showAuthNote)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {showAuthNote ? 'Hide' : 'Show'} authentication details
          </button>
          
          {showAuthNote && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
              <h4 className="font-medium text-gray-900 mb-2">How to Authenticate API Requests</h4>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="font-medium">1.</span>
                  <span>Login via Google OAuth at <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">/api/auth/google/login</code></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium">2.</span>
                  <span>Receive JWT token in response</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium">3.</span>
                  <span>Include token in Authorization header: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">Bearer YOUR_TOKEN</code></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium">4.</span>
                  <span>Test authentication in Swagger UI using the "Authorize" button</span>
                </li>
              </ol>
            </div>
          )}
        </div>

        {/* Usage Tips */}
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-gray-900 mb-1">Development Tips</p>
              <ul className="space-y-1 text-gray-600">
                <li>• Use Swagger UI for testing endpoints during development</li>
                <li>• Export OpenAPI spec to generate client SDKs or Postman collections</li>
                <li>• ReDoc provides better documentation for sharing with external developers</li>
                <li>• Consider restricting access to documentation in production environments</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href="https://swagger.io/tools/swagger-ui/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            Swagger UI Documentation
          </a>
          <span className="text-gray-400">•</span>
          <a
            href="https://github.com/Redocly/redoc"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            ReDoc Documentation
          </a>
          <span className="text-gray-400">•</span>
          <a
            href="https://www.openapis.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            OpenAPI Specification
          </a>
          <span className="text-gray-400">•</span>
          <a
            href="https://fastapi.tiangolo.com/tutorial/metadata/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            FastAPI Docs Guide
          </a>
        </div>
      </div>
    </div>
  )
}

export default ApiDocumentation